import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take, timeout } from "rxjs/operators";

import { AuthenticationService } from "app/auth/service";
import { SearchOrganisationById } from "app/store/platform-config/organisation/actions";
import { organisationById, organisationList } from "app/store/platform-config/organisation/store.module";
import { FileService } from "@core/services/file.service";

export interface OrgBranding {
  name: string;
  logoUrl: string;
  chatbotName: string;
  chatbotLogoUrl: string;
}

/**
 * Resolves the logged-in user's own organisation branding — name + logo and
 * chatbot name + logo (both base64) — scoped to that org rather than whatever
 * org another page may have loaded into the shared store slice. Shared with
 * shareReplay so concurrent consumers that mount together (e.g. the sidebar and
 * the chat widget) share a single SearchOrganisationById dispatch and a single
 * fetch per logo instead of each triggering their own.
 *
 * Emits again whenever the user's organisation entry in the store changes, so a
 * consumer that outlives a navigation (or a stale org list right after login)
 * still ends up on the correct branding.
 */
@Injectable({ providedIn: "root" })
export class OrgBrandingService {
  private _branding$: Observable<OrgBranding> | null = null;

  constructor(
    private store: Store,
    private _authenticationService: AuthenticationService,
    private _fileService: FileService
  ) {
    // Invalidate the cached branding on every login/logout so a new session
    // doesn't reuse the previous user's organisation logo (the shareReplay
    // below would otherwise keep serving it forever, since this service is a
    // root singleton that outlives logout).
    this._authenticationService.currentUser.subscribe(() => {
      this._branding$ = null;
    });
  }

  getCurrentOrgBranding(): Observable<OrgBranding> {
    // refCount so the store subscription of a superseded session's stream is
    // torn down once its last consumer unsubscribes.
    return (this._branding$ ??= this._resolveCurrentOrgBranding().pipe(shareReplay({ bufferSize: 1, refCount: true })));
  }

  private _resolveCurrentOrgBranding(): Observable<OrgBranding> {
    const userOrganisationId = this._authenticationService?.currentUserValue?.organisationId;

    return this.store.select(organisationList).pipe(
      filter((resp) => !!resp),
      map((resp) => resp.find((org) => org.id === userOrganisationId)),
      // Nothing is emitted until the list has loaded, so consumers never flash a
      // fallback logo on a cold start. After a re-login without a page reload the
      // list is briefly the previous session's, which resolves to "no org" — the
      // fresh list then re-triggers resolution for the real org.
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id && prev?.masterOrganisationId === curr?.masterOrganisationId),
      switchMap((organisation) => {
        if (!organisation) {
          return of(this._emptyBranding());
        }

        const name = organisation.name || null;
        const targetId = organisation.masterOrganisationId || organisation.id;

        // Subscribe BEFORE dispatching to catch any cached value already in the
        // store, and also catch the fresh response from the effect. Bounded by
        // a timeout so a slow, failed, or never-resolving org-by-id lookup
        // can't block the sidebar logo forever — it degrades to the branding/
        // default logo fallback instead of leaving the sidebar blank.
        const fullOrg$ = this.store.select(organisationById).pipe(
          filter((orgResp: any) => !!orgResp && orgResp.id === targetId),
          take(1),
          timeout(10000),
          catchError(() => of(null))
        );

        this.store.dispatch(new SearchOrganisationById(targetId));

        return fullOrg$.pipe(
          switchMap((fullOrg: any) => {
            const branding = fullOrg?.branding;
            return forkJoin({
              logoUrl: this._fetchLogo(branding?.organisationLogo),
              chatbotLogoUrl: this._fetchLogo(branding?.chatbotLogo)
            }).pipe(
              map(({ logoUrl, chatbotLogoUrl }) => ({
                name,
                logoUrl,
                chatbotName: branding?.chatbotName || null,
                chatbotLogoUrl
              }))
            );
          })
        );
      })
    );
  }

  /** Base64 logo for a file reference, or null when unset or unfetchable. */
  private _fetchLogo(logoObj: any): Observable<string> {
    if (!logoObj?.url) {
      return of(null);
    }
    return this._fileService.fetchFileBase64(logoObj).pipe(catchError(() => of(null)));
  }

  private _emptyBranding(): OrgBranding {
    return { name: null, logoUrl: null, chatbotName: null, chatbotLogoUrl: null };
  }
}
